import React, { useState } from "react";
import { Button } from "../components/Button.jsx";
import { useGame } from "@empirica/core/player/classic/react";
import { getTaskConfig } from "../utils/taskConfig.js";

export function StoryNogroupInstructions({ next }) {
  const game = useGame();
  const treatment = game?.get("treatment") || {};
  const treatmentName = (
    treatment.name ||
    treatment.treatmentName ||
    treatment.factors?.treatmentName ||
    ""
  ).toLowerCase();
  const isTemplateCondition =
    treatmentName.includes("story-retire-pilot-nogroup-template") ||
    treatmentName.includes("story-tax-pilot-nogroup-template") ||
    treatmentName.includes("story-tax-math-nogroup-template") ||
    treatmentName.includes("story-tax-math-openend-nogroup-template") ||
    treatmentName.includes("story-baserate-nogroup-template");
  const isMathPasteEnabledCondition =
    treatmentName.includes("story-tax-math-socratic-nogroup-norationale") ||
    treatmentName.includes("story-tax-math-nogroup-template") ||
    treatmentName.includes("story-tax-math-openend-socratic-nogroup-norationale") ||
    treatmentName.includes("story-tax-math-openend-nogroup-template") ||
    treatmentName.includes("story-baserate-socratic-nogroup-norationale") ||
    treatmentName.includes("story-baserate-nogroup-template");
  const postConfirmWriteupMode = String(
    treatment.postConfirmWriteupMode ||
      treatment.factors?.postConfirmWriteupMode ||
      ""
  ).toLowerCase();
  const hasPersuasiveWriteupStep = postConfirmWriteupMode === "convince-others";
  const taskConfig = getTaskConfig(game?.get("treatment") || {});
  const hasChangedAnswerConfidence = Boolean(taskConfig?.changedAnswerConfidence);
  const isBinaryConfidenceTask =
    !hasChangedAnswerConfidence &&
    Array.isArray(taskConfig?.options) &&
    taskConfig.options.length === 2 &&
    taskConfig?.confidenceMin === 50 &&
    taskConfig?.confidenceMax === 100;
  const isSpecialConfidenceTask = hasChangedAnswerConfidence || isBinaryConfidenceTask;
  const [answers, setAnswers] = useState({ q1: "", q2: "", q3: "", q4: "" });
  const [showError, setShowError] = useState(false);

  const correctAnswers = {
    q1: "detailed",
    q2: "final",
    ...(isSpecialConfidenceTask
      ? {
          q3: "probability",
          q4: hasChangedAnswerConfidence ? "sum100" : "independent",
        }
      : {}),
  };

  const handleAnswerChange = (question, value) => {
    setAnswers({ ...answers, [question]: value });
    setShowError(false);
  };

  const handleNext = () => {
    const allCorrect = Object.keys(correctAnswers).every(
      (key) => answers[key] === correctAnswers[key]
    );

    if (allCorrect) {
      next();
    } else {
      setShowError(true);
    }
  };

  return (
    <div className="mt-3 sm:mt-5 p-20">
      <h3 className="text-lg leading-6 font-medium text-gray-900">
        Instructions
      </h3>
      <div className="mt-3 mb-6 space-y-6 text-gray-800">
        {/* 1. Objective */}
        <div className="bg-white rounded-md shadow-sm border border-gray-200 p-4">
          <h4 className="font-semibold text-gray-900 mb-3 text-lg">
            1. Objective
          </h4>
          <p>
            Your objective is to make an accurate prediction about a{" "}
            {taskConfig.shortTitle?.toLowerCase() || "prediction"} scenario.
          </p>
          {taskConfig.introPrompt && (
            <p className="mt-2">{taskConfig.introPrompt}</p>
          )}
        </div>

        {/* 2. Process */}
        <div className="bg-white rounded-md shadow-sm border border-gray-200 p-4">
          <h4 className="font-semibold text-gray-900 mb-3 text-lg">
            2. Process
          </h4>
          <div className="space-y-2">
            <p>
              <strong>Step 1:</strong> You will make your{" "}
              <strong>initial prediction</strong> about the scenario.
            </p>
            <p>
              <strong>Step 2:</strong>{" "}
              {isTemplateCondition ? (
                <>
                  You will then complete a <strong>structured follow-up questionnaire</strong> to
                  articulate your reasoning. Please provide{" "}
                  <strong>a detailed explanation</strong> of your reasoning as it
                  will be used to assess the quality of your participation.
                </>
              ) : (
                <>
                  You will then converse with an <strong>AI assistant</strong> who will ask follow-up questions to
                  help you articulate your reasoning. Please provide{" "}
                  <strong>a detailed explanation</strong> of your reasoning as it
                  will be used to assess the quality of your participation.
                </>
              )}
            </p>
            <p>
              <strong>Step 3:</strong>{" "}
              {isTemplateCondition
                ? "After completing the follow-up questionnaire, you will submit your "
                : "After the conversation with the AI assistant, you will submit your "}
              <strong>final prediction</strong>
              {hasChangedAnswerConfidence
                ? " and its confidence. If your final answer changes, you will also report how much confidence you still assign to your initial answer."
                : "."}
            </p>
            {hasPersuasiveWriteupStep && (
              <p>
                <strong>Step 4:</strong> Before continuing, you will write a{" "}
                <strong>detailed persuasive writeup</strong> of at least{" "}
                <strong>50 words</strong> explaining what you would say if you
                were trying to convince others that your estimate is correct.
              </p>
            )}
          </div>
        </div>

        {isSpecialConfidenceTask && (
          <div className="bg-amber-50 rounded-md shadow-sm border border-amber-200 p-4">
            <h4 className="font-semibold text-amber-900 mb-3 text-lg">
              3. Confidence
            </h4>
            <div className="space-y-2 text-amber-900">
              <p>
                In this task, confidence means{" "}
                <strong>
                  "the probability that my chosen answer is correct."
                </strong>
              </p>
              {hasChangedAnswerConfidence ? (
                <>
                  <p>
                    For this task, confidence should be a number from{" "}
                    <strong>0</strong> to <strong>100</strong>.
                  </p>
                  <p>
                    If you change your answer at the final step, you will report
                    two confidence values: one for your final answer and one for
                    your initial answer. Those two numbers should sum to{" "}
                    <strong>100 or less</strong>.
                  </p>
                </>
              ) : (
                <>
                  <p>
                    Because this question has two answer options, confidence must be
                    a whole number from <strong>50</strong> to <strong>100</strong>.
                  </p>
                  <p>
                    Values below 50 are not allowed because they would imply that
                    your unchosen option is more likely to be correct than the one
                    you selected.
                  </p>
                </>
              )}
            </div>
          </div>
        )}

        {isSpecialConfidenceTask && (
          <div className="bg-red-50 rounded-md shadow-sm border border-red-200 p-4">
            <h4 className="font-semibold text-red-900 mb-3 text-lg">
              4. Work On Your Own
            </h4>
            <div className="space-y-2 text-red-900">
              <p>
                This is part of an experiment, so please{" "}
                <strong>work on your own</strong>.
              </p>
              <p>
                Do <strong>not</strong> use ChatGPT, Claude, Gemini, Copilot, or
                any other outside AI or LLM tool while answering this task.
              </p>
              <p>Do not use any outside tools.</p>
              {!isMathPasteEnabledCondition && (
                <p>
                  Copying and pasting are disabled during this task.
                </p>
              )}
            </div>
          </div>
        )}

        {/* 3. Reward setup */}
        <div className="bg-blue-50 rounded-md shadow-sm border border-blue-200 p-4">
          <h4 className="font-semibold text-blue-900 mb-3 text-lg">
            {isSpecialConfidenceTask ? "5. Reward Setup" : "3. Reward Setup"}
          </h4>
          <div className="space-y-2 text-blue-800">
            <p>
              <strong>Base Payment:</strong> You will receive $1.5.
            </p>
            <p>
              <strong>Accuracy Bonus:</strong> In addition to the base payment,
              you may receive <strong>$2</strong> if your{" "}
              <strong>final prediction</strong> is accurate.
            </p>
          </div>
        </div>

        {/* Comprehension Questions */}
        <div className="bg-yellow-50 rounded-md shadow-sm border border-yellow-200 p-4">
          <h4 className="font-semibold text-yellow-900 mb-3 text-lg">
            Please answer the following questions to proceed:
          </h4>

          <div className="space-y-4">
            {/* Question 1 */}
            <div>
              <p className="font-medium text-gray-900 mb-2">
                {isTemplateCondition
                  ? "1. What should you do when you complete the follow-up questionnaire?"
                  : "1. What should you do when you converse with the AI assistant?"}
              </p>
              <div className="space-y-1 ml-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="q1"
                    value="brief"
                    checked={answers.q1 === "brief"}
                    onChange={(e) => handleAnswerChange("q1", e.target.value)}
                    className="mr-2"
                  />
                  <span>Provide a brief one-sentence explanation</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="q1"
                    value="detailed"
                    checked={answers.q1 === "detailed"}
                    onChange={(e) => handleAnswerChange("q1", e.target.value)}
                    className="mr-2"
                  />
                  <span>
                    Explain your rationale and provide a detailed explanation of
                    your reasoning
                  </span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="q1"
                    value="skip"
                    checked={answers.q1 === "skip"}
                    onChange={(e) => handleAnswerChange("q1", e.target.value)}
                    className="mr-2"
                  />
                  <span>
                    {isTemplateCondition
                      ? "Skip the questionnaire and go directly to the final prediction"
                      : "Skip the conversation and go directly to the final prediction"}
                  </span>
                </label>
              </div>
            </div>

            {/* Question 2 */}
            <div>
              <p className="font-medium text-gray-900 mb-2">
                2. The $2 accuracy bonus is based on which outcome?
              </p>
              <div className="space-y-1 ml-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="q2"
                    value="initial"
                    checked={answers.q2 === "initial"}
                    onChange={(e) => handleAnswerChange("q2", e.target.value)}
                    className="mr-2"
                  />
                  <span>Your initial prediction</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="q2"
                    value="final"
                    checked={answers.q2 === "final"}
                    onChange={(e) => handleAnswerChange("q2", e.target.value)}
                    className="mr-2"
                  />
                  <span>Your final prediction</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="q2"
                    value="both"
                    checked={answers.q2 === "both"}
                    onChange={(e) => handleAnswerChange("q2", e.target.value)}
                    className="mr-2"
                  />
                  <span>Both your initial and final predictions</span>
                </label>
              </div>
            </div>

            {isSpecialConfidenceTask && (
              <div>
                <p className="font-medium text-gray-900 mb-2">
                  3. In this task, what does confidence mean?
                </p>
                <div className="space-y-1 ml-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="q3"
                      value="certainty"
                      checked={answers.q3 === "certainty"}
                      onChange={(e) => handleAnswerChange("q3", e.target.value)}
                      className="mr-2"
                    />
                    <span>How strongly I feel in a general sense</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="q3"
                      value="probability"
                      checked={answers.q3 === "probability"}
                      onChange={(e) => handleAnswerChange("q3", e.target.value)}
                      className="mr-2"
                    />
                    <span>The probability that my chosen answer is correct</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="q3"
                      value="speed"
                      checked={answers.q3 === "speed"}
                      onChange={(e) => handleAnswerChange("q3", e.target.value)}
                      className="mr-2"
                    />
                    <span>How quickly I answered the question</span>
                  </label>
                </div>
              </div>
            )}

            {isSpecialConfidenceTask && (
              <div>
                <p className="font-medium text-gray-900 mb-2">
                  {hasChangedAnswerConfidence
                    ? "4. If you change your final answer, what should happen to the two confidence values?"
                    : "4. Which type of assistance is allowed while answering?"}
                </p>
                <div className="space-y-1 ml-4">
                  {hasChangedAnswerConfidence ? (
                    <>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="q4"
                          value="sum100"
                          checked={answers.q4 === "sum100"}
                          onChange={(e) => handleAnswerChange("q4", e.target.value)}
                          className="mr-2"
                        />
                        <span>
                          The confidence in my final answer and the confidence in
                          my initial answer should sum to 100 or less.
                        </span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="q4"
                          value="equal"
                          checked={answers.q4 === "equal"}
                          onChange={(e) => handleAnswerChange("q4", e.target.value)}
                          className="mr-2"
                        />
                        <span>
                          The two confidence values must always sum to exactly 100.
                        </span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="q4"
                          value="ignore"
                          checked={answers.q4 === "ignore"}
                          onChange={(e) => handleAnswerChange("q4", e.target.value)}
                          className="mr-2"
                        />
                        <span>
                          I only need to report confidence in the final answer.
                        </span>
                      </label>
                    </>
                  ) : (
                    <>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="q4"
                          value="independent"
                          checked={answers.q4 === "independent"}
                          onChange={(e) => handleAnswerChange("q4", e.target.value)}
                          className="mr-2"
                        />
                        <span>
                          Only the built-in study interface is allowed. No outside
                          AI or other outside help.
                        </span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="q4"
                          value="llm"
                          checked={answers.q4 === "llm"}
                          onChange={(e) => handleAnswerChange("q4", e.target.value)}
                          className="mr-2"
                        />
                        <span>
                          It is fine to use ChatGPT or another outside AI tool if I
                          still submit my own answer
                        </span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="q4"
                          value="paste"
                          checked={answers.q4 === "paste"}
                          onChange={(e) => handleAnswerChange("q4", e.target.value)}
                          className="mr-2"
                        />
                        <span>
                          I can paste the prompt into an external tool as long as I
                          decide by myself in the end
                        </span>
                      </label>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {showError && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-800 text-sm">
                Please answer all questions correctly before proceeding.
              </p>
            </div>
          )}
        </div>
      </div>
      <Button handleClick={handleNext} autoFocus>
        <p>Next</p>
      </Button>
    </div>
  );
}
